import React, { useCallback, useMemo } from 'react'
import type { Entry } from 'contentful'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'

import Card, { type CardProps } from '@/components/ui/card'
import RichText from '@/components/ui/rich-text'
import DynamicAction, { type DynamicActionProps } from '@/components/ui/dynamic-action'

import { cx, slugify } from '@/utils/strings'

import { Experience } from '@ninetailed/experience.js-react'
import { mapNtExperiences } from '@/utils/ninetailed'

import PricingCard, { type PricingCardProps } from '@/components/ui/pricing-card'
import Image from '@/components/ui/image'
import CodeTabs from '@/components/ui/code/code-tabs'
import Quote from '@/components/ui/quote'
import {
  isTypeComponentCard,
  type TypeBlogPageSkeleton,
  type TypeComponentCardSkeleton,
  type TypeComponentPricingSkeleton,
  type TypeLandingPageSkeleton,
  type TypeResourceImageSkeleton,
  type TypeResourcePageSkeleton,
  type TypeResourceVideoPageSkeleton,
  type TypeSectionSetOfCardsSkeleton,
  type TypeComponentQuoteSkeleton,
} from '@/types/ctf'
import ShieldLoader from '@/components/ui/shield-loader'
import GradientBackgroundWrapper from '@/components/ui/gradient-background-wrapper'
import SectionHeading from '@/components/ui/section-heading'
import { getHeaderHeight } from '@/utils/dom'
import { useInspectorProps } from '@/utils/live-preview'

type CardEntry = Entry<TypeComponentCardSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type BlogPageEntry = Entry<TypeBlogPageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type ResourcePageEntry = Entry<TypeResourcePageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type SetOfCardsEntry = Entry<TypeSectionSetOfCardsSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>

export type SetOfCardsProps = SetOfCardsEntry['fields'] & {
  entryId?: string
  isHero?: boolean
  pageOrderNo?: number
  isPrintView?: boolean
  filter?: {
    search?: string
    tags?: string[]
  }
  imageType?: string
  backgroundStyle?: string
  hideHeadline?: boolean
}

export const SetOfCardsSectionComponent = ({
  entryId,
  isHero,
  pageOrderNo,
  isPrintView,
  actionsPosition,
  headline,
  headingLayout = 'Centered',
  anchorLinkTitle,
  body,
  cards,
  actions,
  backgroundStyle,
  cardIconSize,
  cardIconLocation,
  cardsPerColumn,
  excludeFromPdf,
  filter,
  imageType,
  heroLayout = 'Horizontal',
  eyebrowText,
  hideHeadline = false,
  unconstrainedBackground,
  assetImageBelowText,
  enableCardLinksForLabeledCta = false,
}: SetOfCardsProps) => {
  const inspectorProps = useInspectorProps({ entryId, restrictedTypes: ['sectionSetOfCards'] })
  const pageInspectorProps = useInspectorProps({ entryId, restrictedTypes: ['pages'] })

  const columnCount = cardsPerColumn || (Array.isArray(cards) && cards?.length <= 6 ? cards?.length : 3) || 1

  const renderActions = useCallback(
    () => (
      <div
        className={cx(
          'flex flex-wrap items-center gap-4',
          headingLayout === 'Centered' && 'justify-center',
          (headline || body) && (cards?.length ?? 0) === 0 && 'mt-8'
        )}
        {...inspectorProps({ fieldId: 'actions' })}
      >
        {actions?.map((action) => {
          if (!action) return null
          const type = action.sys.contentType.sys.id
          const fields = {
            type,
            ...action.fields,
          } as DynamicActionProps
          return <DynamicAction key={action?.sys.id} id={action?.sys.id} {...fields} data-testid="card-cta" />
        })}
      </div>
    ),
    [actions, headingLayout, headline, body, cards]
  )

  // Grid wrapper component that only renders grid container when needed
  const GridWrapper = useCallback(
    ({ children }: { children: React.ReactNode }) => {
      if (columnCount >= 2) {
        return (
          <div
            className={cx(
              'grid gap-x-6 gap-y-6 xl:gap-y-12',
              columnCount === 2 && `lg:grid-cols-2 xl:gap-x-8`,
              columnCount === 3 && `lg:grid-cols-3 xl:gap-x-8`,
              columnCount === 4 && `lg:grid-cols-4 xl:gap-x-8`,
              columnCount === 5 && `lg:grid-cols-5 xl:gap-x-6`,
              columnCount === 6 && `lg:grid-cols-6 xl:gap-x-6`,
              actions && `mb-10`
            )}
          >
            {children}
          </div>
        )
      }
      return (
        <div
          className={cx(
            (cards?.length ?? 0) >= 2 && 'flex flex-col gap-6 lg:gap-12',
            (cards?.length ?? 0) >= 2 && backgroundStyle && 'py-10 lg:py-20'
          )}
        >
          {children}
        </div>
      )
    },
    [columnCount, actions, cards, backgroundStyle]
  )

  const shouldFilter = typeof filter !== 'undefined'

  // Memoize the filtered cards computation to prevent recalculation on every render
  const filteredCards = useMemo(() => {
    if (!shouldFilter) return []

    return (
      cards?.map((card) => {
        if (!card) return true

        if (isTypeComponentCard(card)) {
          // Filter by search term
          if (filter.search) {
            const searchTerms = filter.search.toLowerCase().split(' ').filter(Boolean)
            const subtext =
              typeof card.fields.subtext !== 'string'
                ? card.fields.subtext
                  ? documentToPlainTextString(card.fields.subtext)
                  : ''
                : card.fields.subtext || ''
            if (
              searchTerms.every(
                (term) => !card.fields.headline?.toLowerCase().includes(term) && !subtext.toLowerCase().includes(term)
              )
            ) {
              return true
            }
          }
          // Filter by active filter tags
          if (filter.tags && filter.tags.length > 0) {
            const tags = card.fields.filterTags
            // Hide card if it doesn't have any of the active filters
            if (!tags || !tags.some((tag) => filter.tags?.includes(tag))) {
              return true
            }
          }
        }

        return false
      }) || []
    )
  }, [cards, filter, shouldFilter])

  const areAllCardsFiltered = shouldFilter && filteredCards.every((card) => card)

  // Memoize card props to prevent object recreation on every render
  const cardIconLocationForIndex = useMemo(
    () => (idx: number) =>
      columnCount === 1 && idx % 2 === 1
        ? ({ Right: 'Left', Left: 'Right' }[cardIconLocation] as typeof cardIconLocation)
        : cardIconLocation,
    [columnCount, cardIconLocation]
  )

  const baseCardProps = useMemo(
    () => ({
      cardIconSize,
      columnCount,
      isHero: isHero || false,
      imageType,
      heroLayout,
      sectionHasHeadline: !!headline,
      backgroundStyle,
      assetImageBelowText: assetImageBelowText,
    }),
    [cardIconSize, columnCount, isHero, imageType, heroLayout, headline, backgroundStyle, assetImageBelowText]
  )

  // Memoize card props computation to prevent creating new action arrays on every render
  const processedCards = useMemo(() => {
    if (!cards) return []

    return cards.map((card, idx) => {
      if (!card) return null

      let cardProps: Partial<CardProps> = {}

      // Create a scope-isolated actions variable for the switch statement
      let actionsForCard: DynamicActionProps[] = []

      switch (card.sys.contentType.sys.id) {
        case 'componentCard': {
          cardProps = card.fields as CardProps

          // Hide card if it's filtered out
          if (filteredCards[idx]) {
            cardProps.className = 'hidden'
          } else {
            cardProps.className = ''
          }

          const actions = (card.fields as CardEntry['fields']).actions as
            | CardEntry['fields']['actions']
            | DynamicActionProps[]
          if (actions) {
            actionsForCard = actions
              .map((action) => {
                if (!action) return null
                if ((action as DynamicActionProps).type) return action as DynamicActionProps
                const type = action.sys?.contentType.sys.id
                const fields = {
                  type,
                  id: action.sys.id,
                  ...action.fields,
                } as DynamicActionProps
                return fields
              })
              .filter((action) => !!action) as DynamicActionProps[]
          }
          cardProps.actions = actionsForCard
          break
        }
        case 'blogPage': {
          const blogCard = card as BlogPageEntry
          actionsForCard = [
            {
              id: blogCard.sys.id,
              to: `/blog/${blogCard.fields.slug}`,
            },
          ]
          cardProps = {
            tagline: 'Blog',
            headline: blogCard.fields.title,
            subtext: blogCard.fields.description,
            image: blogCard.fields?.image?.fields?.image,
            imageDarkMode: blogCard.fields?.image?.fields.darkModeImage,
            imageStyle: (blogCard.fields as any).imageStyle,
            tags: blogCard.fields.tags,
            actions: actionsForCard,
          }
          break
        }
        case 'resourcePage': {
          const resourceCard = card as ResourcePageEntry
          actionsForCard = [
            {
              type: 'ctaLink',
              id: resourceCard.sys.id,
              to: `/resources/${resourceCard.fields.slug}`,
            },
          ]
          cardProps = {
            tagline: 'Resource',
            headline: resourceCard.fields.title,
            subtext: resourceCard.fields.description,
            image: resourceCard.fields?.heroImage?.fields?.image,
            imageDarkMode: resourceCard.fields?.heroImage?.fields?.darkModeImage,
            tags: resourceCard.fields.resourceTags?.map((tag) => tag?.fields?.name).filter(Boolean) as string[],
            actions: actionsForCard,
          }
          break
        }
        case 'resourceVideoPage': {
          const resourceVideoCard = card as Entry<TypeResourceVideoPageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
          actionsForCard = [
            {
              type: 'ctaLink',
              id: resourceVideoCard.sys.id,
              to: `/resources/videos/${resourceVideoCard.fields.slug}`,
            },
          ]
          cardProps = {
            tagline: 'Video',
            headline: resourceVideoCard.fields.title,
            subtext: resourceVideoCard.fields.image?.fields.description,
            image: resourceVideoCard.fields.image || resourceVideoCard.fields.seo?.fields.ogImage,
            actions: actionsForCard,
          }
          break
        }
        case 'landingPage': {
          const pageCard = card as Entry<TypeLandingPageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
          actionsForCard = [
            {
              type: 'ctaLink',
              id: pageCard.sys.id,
              to: `/${pageCard.fields.slug}`,
            },
          ]
          cardProps = {
            headline: pageCard?.fields.seo?.fields.title,
            subtext: pageCard?.fields.seo?.fields.description,
            image: pageCard?.fields.seo?.fields.ogImage,
            actions: actionsForCard,
          }
          break
        }
        case 'componentCodeTabs':
          return <CodeTabs key={card.sys.id} code={(card as any).fields.code} />
        case 'componentPricing': {
          const {
            fields: { price, features, actions: pricingCardActions, ...pricingCard },
          } = card as Entry<TypeComponentPricingSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
          const pricingCardProps: PricingCardProps = {
            ...pricingCard,
            price,
            features: features as string[],
            actions: pricingCardActions,
            hasShadow: true,
          }
          return <PricingCard key={card.sys.id} {...pricingCardProps} />
        }
        case 'resourceImage': {
          return (
            <Image
              key={card.sys.id}
              {...(card.fields as Entry<TypeResourceImageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>['fields'])}
            />
          )
        }
        case 'componentQuote':
          return (
            <Quote
              key={card.sys.id}
              {...(card.fields as Entry<TypeComponentQuoteSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>['fields'])}
            />
          )
        default:
          break
      }

      return {
        id: card.sys.id,
        cardProps: {
          entryId: card.sys.id,
          ...cardProps,
          ...baseCardProps,
          cardIconLocation: cardIconLocationForIndex(idx),
          enableCardLinkForLabeledCta: enableCardLinksForLabeledCta,
        },
      }
    })
  }, [cards, filteredCards, baseCardProps, cardIconLocationForIndex, enableCardLinksForLabeledCta])

  if (areAllCardsFiltered) return null

  return isPrintView ? (
    !excludeFromPdf && (
      <div className="py-5 break-inside-avoid">
        {headline && <h2 className={cx(!body && 'py-3')}>{headline}</h2>}
        {body && (
          <div className="[&_p]:text-[9pt]! [&_a]:text-[9pt]!">
            <RichText body={body} />
          </div>
        )}
        <div
          className={cx(
            (cards?.length ?? 0) > 1 && 'grid grid-cols-2 gap-x-6',
            (cards?.length ?? 0) === 6 && 'grid grid-cols-3 gap-x-6',
            (cards?.length ?? 0) === 3 && 'grid grid-cols-3 gap-x-4'
          )}
        >
          {processedCards.map((card) => {
            if (!card) return null
            if (React.isValidElement(card)) return card

            const cardItem = card as { id: string; cardProps: CardProps }
            return <Card key={cardItem.id} {...cardItem.cardProps} />
          })}
        </div>
      </div>
    )
  ) : (
    <GradientBackgroundWrapper
      id={headline ? slugify(headline) : entryId}
      backgroundStyle={backgroundStyle}
      containerClassName={cx(
        isHero && 'cards-hero',
        !backgroundStyle &&
          cx('max-w-8xl mx-auto relative px-5 lg:px-9', cardIconSize !== '1/2 (Bleed)' && 'overflow-x-clip')
      )}
      innerClassName={cx(backgroundStyle && (unconstrainedBackground ? 'py-6 lg:py-16' : 'px-5 lg:px-9 py-6 lg:py-16'))}
      {...pageInspectorProps({ fieldId: 'eyebrowText' })}
      unconstrainedBackground={unconstrainedBackground}
    >
      <SectionHeading
        headline={headline}
        body={body}
        headingLayout={headingLayout}
        columnCount={columnCount}
        isHero={isHero}
        eyebrowText={eyebrowText}
        hideHeadline={hideHeadline}
        actions={!actionsPosition || actionsPosition === 'Above cards' ? actions : undefined}
        anchorLinkTitle={anchorLinkTitle}
        pageOrderNo={pageOrderNo}
        entryId={entryId}
      />

      {cards && (
        <GridWrapper>
          {processedCards.map((card) => {
            if (!card) return null
            if (React.isValidElement(card)) return card

            const cardItem = card as { id: string; cardProps: CardProps }
            return <Card key={cardItem.id} id={cardItem.id} {...cardItem.cardProps} />
          })}
        </GridWrapper>
      )}

      {actions && actionsPosition === 'Below cards' && renderActions()}
    </GradientBackgroundWrapper>
  )
}

export default function SetOfCardsSection(props: SetOfCardsProps): React.JSX.Element {
  const { nt_experiences, id = '', isHero, pageOrderNo, filter } = props

  const ShieldBeatLoader = useMemo(() => {
    return () => (
      <div style={{ minHeight: isHero ? `calc(100vh - ${getHeaderHeight()}px)` : undefined }}>
        <ShieldLoader containerHeight="500" />
      </div>
    )
  }, [])
  return (
    <Experience
      {...props}
      id={id}
      experiences={mapNtExperiences(nt_experiences)}
      component={SetOfCardsSectionComponent}
      passthroughProps={{ isHero, pageOrderNo, filter }}
      loadingComponent={ShieldBeatLoader}
    />
  )
}
